const escapeHtml = (value) => String(value ?? '')
  .replaceAll('&', '&amp;')
  .replaceAll('<', '&lt;')
  .replaceAll('>', '&gt;')
  .replaceAll('"', '&quot;')
  .replaceAll("'", '&#039;');

const toNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const formatNumber = (value) => new Intl.NumberFormat('es-ES').format(toNumber(value));

const formatCurrency = (value) => new Intl.NumberFormat('es-ES', {
  style: 'currency',
  currency: 'EUR'
}).format(toNumber(value));

const getColorData = (color, colorSettings) => {
  const configuredColor = colorSettings?.[color];
  const configuredHex = typeof configuredColor === 'string' ? configuredColor : configuredColor?.hex;
  const safeHex = /^#[0-9a-f]{3,8}$/i.test(configuredHex || '') ? configuredHex : '#d1d5db';

  if (typeof configuredColor === 'string') {
    return { hex: safeHex, label: color };
  }

  return {
    hex: safeHex,
    label: configuredColor?.description || color
  };
};

const renderIndicator = (label, value, modifier = '') => `
  <div class="indicator ${modifier}">
    <span>${escapeHtml(label)}</span>
    <strong>${formatNumber(value)}</strong>
  </div>
`;

const getConfiguredInventoryRows = (product, productInventory) => {
  const sizes = new Set((Array.isArray(product.sizes) ? product.sizes : []).map(String));
  const colors = new Set((Array.isArray(product.colors) ? product.colors : []).map(String));

  return productInventory.filter(item =>
    sizes.has(String(item?.size)) && colors.has(String(item?.color))
  );
};

const renderProduct = (product, productInventory, colorSettings) => {
  const configuredInventory = getConfiguredInventoryRows(product, productInventory);
  const inventoryMap = new Map(
    configuredInventory.map(item => [`${item.color}::${item.size}`, item])
  );
  const physicalStock = configuredInventory.reduce((total, item) => total + toNumber(item.quantity), 0);
  const pendingStock = configuredInventory.reduce((total, item) => total + toNumber(item.pending_orders), 0);
  const availableStock = physicalStock - pendingStock;
  const image = Array.isArray(product.images)
    ? product.images[product.main_image_index || 0] || product.images[0]
    : null;
  const sizes = Array.isArray(product.sizes) ? product.sizes : [];
  const colors = Array.isArray(product.colors) ? product.colors : [];

  const stockRows = colors.map(color => {
    const colorData = getColorData(color, colorSettings);
    const cells = sizes.map(size => {
      const item = inventoryMap.get(`${color}::${size}`);
      const available = toNumber(item?.quantity) - toNumber(item?.pending_orders);
      const stockClass = available > 0 ? 'positive' : 'negative';
      return `<td class="stock-value ${stockClass}">${formatNumber(available)}</td>`;
    }).join('');

    return `
      <tr>
        <th scope="row" class="color-cell">
          <span class="color-dot" style="background:${escapeHtml(colorData.hex)}"></span>
          <span>${escapeHtml(colorData.label)}</span>
        </th>
        ${cells}
      </tr>
    `;
  }).join('');

  const sizeHeaders = sizes.map(size => `<th scope="col">${escapeHtml(size)}</th>`).join('');
  const sizeBadges = sizes.map(size => `<span>${escapeHtml(size)}</span>`).join('');
  const colorBadges = colors.map(color => {
    const colorData = getColorData(color, colorSettings);
    return `
      <div class="color-badge">
        <span class="color-dot" style="background:${escapeHtml(colorData.hex)}"></span>
        <small>${escapeHtml(colorData.label)}</small>
      </div>
    `;
  }).join('');

  return `
    <article class="product-card">
      <div class="product-image-wrap">
        ${image
          ? `<img src="${escapeHtml(image)}" alt="${escapeHtml(product.description || product.code)}">`
          : '<div class="no-image">Sin imagen</div>'}
      </div>

      <section class="product-details">
        <div class="product-heading">
          <h2>${escapeHtml(product.code)}</h2>
          ${product.active === false ? '<span class="status inactive">Inactivo</span>' : ''}
          ${product.venta_solo_stock ? '<span class="status stock-only">Solo venta stock</span>' : ''}
        </div>
        <p class="description">${escapeHtml(product.description)}</p>
        <p class="price">${formatCurrency(product.price)}</p>
        ${product.temporada ? `<p><b>Temporada:</b> ${escapeHtml(product.temporada)}</p>` : ''}
        ${product.composicion ? `<p class="composition"><b>Composición:</b> ${escapeHtml(product.composicion)}</p>` : ''}
        <div class="sizes" translate="no">${sizeBadges}</div>
        <div class="colors">${colorBadges}</div>
      </section>

      <section class="product-stock">
        <div class="stock-heading">
          <h3>Stock por color y talla</h3>
          <span>Disponible = físico − pedidos pendientes</span>
        </div>
        <div class="product-indicators">
          ${renderIndicator('Stock físico', physicalStock)}
          ${renderIndicator('Pedidos pendientes', pendingStock, 'pending')}
          ${renderIndicator('Stock disponible', availableStock, availableStock > 0 ? 'available' : 'unavailable')}
        </div>
        ${sizes.length > 0 && colors.length > 0 ? `
          <table>
            <thead>
              <tr><th scope="col">Color</th>${sizeHeaders}</tr>
            </thead>
            <tbody>${stockRows}</tbody>
          </table>
        ` : '<p class="empty-stock">El artículo no tiene colores o tallas configurados.</p>'}
      </section>
    </article>
  `;
};

export function buildStockCatalogHtml({
  products,
  inventory,
  colorSettings = {},
  filterDescriptions = [],
  printedAt = new Date()
}) {
  const inventoryByProduct = new Map();

  inventory.forEach(item => {
    const rows = inventoryByProduct.get(item.product_id) || [];
    rows.push(item);
    inventoryByProduct.set(item.product_id, rows);
  });

  const totals = products.reduce((result, product) => {
    const configuredInventory = getConfiguredInventoryRows(product, inventoryByProduct.get(product.id) || []);

    configuredInventory.forEach(item => {
      result.physical += toNumber(item.quantity);
      result.pending += toNumber(item.pending_orders);
    });

    return result;
  }, { physical: 0, pending: 0 });
  const totalAvailable = totals.physical - totals.pending;
  const printedAtLabel = new Intl.DateTimeFormat('es-ES', {
    dateStyle: 'short',
    timeStyle: 'medium'
  }).format(printedAt);
  const filters = filterDescriptions.length > 0
    ? filterDescriptions.map(filter => `<span>${escapeHtml(filter)}</span>`).join('')
    : '<span>Sin filtros</span>';
  const productCards = products.map(product => renderProduct(
    product,
    inventoryByProduct.get(product.id) || [],
    colorSettings
  )).join('');

  return `<!doctype html>
  <html lang="es">
    <head>
      <meta charset="utf-8">
      <meta name="viewport" content="width=device-width, initial-scale=1">
      <title>Catálogo de stock</title>
      <style>
        :root { color-scheme: light; }
        * { box-sizing: border-box; }
        body {
          margin: 0;
          background: #eef2f7;
          color: #111827;
          font-family: Inter, ui-sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        .toolbar {
          position: sticky;
          top: 0;
          z-index: 10;
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 16px;
          padding: 12px 24px;
          background: #111827;
          color: white;
          box-shadow: 0 2px 8px rgb(15 23 42 / 20%);
        }
        .toolbar p { margin: 2px 0 0; color: #cbd5e1; font-size: 12px; }
        .toolbar button {
          border: 0;
          border-radius: 8px;
          padding: 10px 18px;
          background: #2563eb;
          color: white;
          font: inherit;
          font-weight: 700;
          cursor: pointer;
        }
        .toolbar button:disabled { background: #64748b; cursor: wait; }
        .catalog { width: min(1380px, calc(100% - 32px)); margin: 18px auto 40px; }
        .catalog-header {
          padding: 20px 22px;
          margin-bottom: 14px;
          border: 1px solid #dbe3ee;
          border-radius: 12px;
          background: white;
        }
        .title-row { display: flex; justify-content: space-between; gap: 24px; align-items: flex-start; }
        h1 { margin: 0; font-size: 26px; letter-spacing: -.02em; }
        .print-date { margin: 4px 0 0; color: #64748b; font-size: 12px; text-align: right; }
        .filters { display: flex; align-items: center; flex-wrap: wrap; gap: 7px; margin-top: 13px; }
        .filters b { margin-right: 2px; font-size: 12px; }
        .filters span { padding: 4px 8px; border-radius: 999px; background: #eff6ff; color: #1d4ed8; font-size: 11px; }
        .summary {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 10px;
          margin-top: 16px;
        }
        .summary .indicator { padding: 11px 13px; }
        .summary .indicator strong { font-size: 21px; }
        .indicator {
          display: flex;
          flex-direction: column;
          gap: 2px;
          min-width: 0;
          border: 1px solid #dbe3ee;
          border-radius: 8px;
          padding: 7px 9px;
          background: #f8fafc;
        }
        .indicator span { color: #64748b; font-size: 10px; font-weight: 600; }
        .indicator strong { font-size: 16px; line-height: 1.1; }
        .indicator.pending strong { color: #b45309; }
        .indicator.available strong { color: #15803d; }
        .indicator.unavailable strong { color: #dc2626; }
        .product-card {
          display: grid;
          grid-template-columns: 220px minmax(190px, .8fr) minmax(430px, 1.7fr);
          gap: 18px;
          min-height: 274px;
          margin-bottom: 14px;
          padding: 16px;
          overflow: hidden;
          border: 1px solid #dbe3ee;
          border-radius: 12px;
          background: white;
          box-shadow: 0 1px 3px rgb(15 23 42 / 7%);
          break-inside: avoid;
          page-break-inside: avoid;
        }
        .product-image-wrap {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          min-height: 240px;
          overflow: hidden;
          border-radius: 9px;
          background: #f8fafc;
        }
        .product-image-wrap img {
          display: block;
          width: 100%;
          height: 250px;
          object-fit: contain;
          object-position: center;
        }
        .no-image { color: #94a3b8; font-size: 12px; }
        .product-details { min-width: 0; }
        .product-heading { display: flex; align-items: center; flex-wrap: wrap; gap: 7px; }
        .product-heading h2 { margin: 0; font-size: 21px; }
        .status { padding: 3px 7px; border-radius: 999px; font-size: 9px; font-weight: 700; }
        .status.inactive { background: #fee2e2; color: #991b1b; }
        .status.stock-only { background: #ffedd5; color: #9a3412; }
        .description { margin: 5px 0 8px; color: #475569; font-size: 13px; font-weight: 600; }
        .price { margin: 0 0 9px; font-size: 16px; font-weight: 800; }
        .product-details > p:not(.description):not(.price) { margin: 5px 0; color: #475569; font-size: 11px; }
        .composition { white-space: pre-line; }
        .sizes { display: flex; flex-wrap: wrap; gap: 5px; margin-top: 11px; }
        .sizes span { padding: 4px 7px; border-radius: 5px; background: #f1f5f9; font-size: 10px; font-weight: 800; }
        .colors { display: flex; flex-wrap: wrap; gap: 9px; margin-top: 11px; }
        .color-badge { display: flex; flex-direction: column; align-items: center; gap: 3px; }
        .color-badge small { max-width: 62px; color: #334155; font-size: 9px; text-align: center; }
        .color-dot { width: 18px; height: 18px; flex: 0 0 auto; border: 1px solid #94a3b8; border-radius: 50%; }
        .product-stock { min-width: 0; }
        .stock-heading { display: flex; justify-content: space-between; align-items: baseline; gap: 12px; margin-bottom: 8px; }
        .stock-heading h3 { margin: 0; font-size: 14px; }
        .stock-heading span { color: #64748b; font-size: 9px; text-align: right; }
        .product-indicators { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 6px; margin-bottom: 9px; }
        table { width: 100%; border-collapse: collapse; table-layout: fixed; font-size: 10px; }
        th, td { padding: 6px 4px; border: 1px solid #e2e8f0; text-align: center; }
        thead th { background: #f8fafc; font-weight: 800; }
        thead th:first-child { width: 92px; text-align: left; }
        .color-cell { background: white; text-align: left; font-weight: 600; }
        .color-cell .color-dot { display: inline-block; width: 14px; height: 14px; margin-right: 5px; vertical-align: middle; }
        .color-cell span:last-child { vertical-align: middle; }
        .stock-value { font-weight: 700; }
        .stock-value.positive { color: #15803d; }
        .stock-value.negative { color: #9ca3af; }
        .empty-stock { padding: 18px; border: 1px dashed #cbd5e1; border-radius: 8px; color: #64748b; font-size: 11px; text-align: center; }
        .empty-catalog { padding: 40px; border: 1px dashed #cbd5e1; border-radius: 12px; background: white; color: #64748b; text-align: center; }
        .catalog-footer { margin-top: 16px; color: #64748b; font-size: 10px; text-align: center; }

        @media (max-width: 900px) {
          .product-card { grid-template-columns: 180px 1fr; }
          .product-stock { grid-column: 1 / -1; }
          .summary { grid-template-columns: repeat(2, minmax(0, 1fr)); }
        }

        @page { size: A4 landscape; margin: 9mm; }
        @media print {
          body { background: white; }
          .toolbar { display: none !important; }
          .catalog { width: 100%; margin: 0; }
          .catalog-header { padding: 12px 14px; margin-bottom: 8px; border-radius: 0; }
          h1 { font-size: 20px; }
          .summary { margin-top: 10px; }
          .summary .indicator { padding: 7px 9px; }
          .summary .indicator strong { font-size: 17px; }
          .product-card {
            grid-template-columns: 205px minmax(175px, .75fr) minmax(410px, 1.8fr);
            gap: 13px;
            min-height: 252px;
            margin-bottom: 8px;
            padding: 11px;
            border-radius: 0;
            box-shadow: none;
          }
          .product-image-wrap { min-height: 225px; }
          .product-image-wrap img { height: 230px; }
          .catalog-footer { margin-top: 8px; }
        }
      </style>
    </head>
    <body>
      <div class="toolbar">
        <div>
          <strong>Vista previa del catálogo de stock</strong>
          <p>Revisa el contenido antes de abrir el diálogo de impresión.</p>
        </div>
        <button id="print-button" type="button" onclick="printCatalog()" disabled>Cargando imágenes…</button>
      </div>

      <main class="catalog">
        <header class="catalog-header">
          <div class="title-row">
            <div>
              <h1>Catálogo de stock</h1>
              <div class="filters"><b>Filtros:</b>${filters}</div>
            </div>
            <p class="print-date"><b>Fecha y hora de impresión</b><br><span class="print-date-value">${escapeHtml(printedAtLabel)}</span></p>
          </div>
          <div class="summary">
            ${renderIndicator('Artículos', products.length)}
            ${renderIndicator('Stock físico', totals.physical)}
            ${renderIndicator('Pedidos pendientes', totals.pending, 'pending')}
            ${renderIndicator('Stock disponible', totalAvailable, totalAvailable > 0 ? 'available' : 'unavailable')}
          </div>
        </header>

        ${productCards || '<div class="empty-catalog">No hay artículos que coincidan con los filtros seleccionados.</div>'}
        <footer class="catalog-footer">Catálogo de stock generado el <span class="print-date-value">${escapeHtml(printedAtLabel)}</span></footer>
      </main>
      <script>
        function refreshPrintDate() {
          const value = new Intl.DateTimeFormat('es-ES', {
            dateStyle: 'short',
            timeStyle: 'medium'
          }).format(new Date());
          document.querySelectorAll('.print-date-value').forEach(element => {
            element.textContent = value;
          });
        }

        function printCatalog() {
          refreshPrintDate();
          window.print();
        }

        const images = Array.from(document.images);
        const imageLoads = images.map(image => image.complete
          ? Promise.resolve()
          : new Promise(resolve => {
              image.addEventListener('load', resolve, { once: true });
              image.addEventListener('error', resolve, { once: true });
            })
        );

        Promise.all(imageLoads).then(() => {
          const button = document.getElementById('print-button');
          button.disabled = false;
          button.textContent = 'Imprimir catálogo';
        });
        window.addEventListener('beforeprint', refreshPrintDate);
      </script>
    </body>
  </html>`;
}
